import React, { useState, useCallback } from "react";
import { Confirm } from "semantic-ui-react";
import ServerDataGrid from "./ServerDataGrid";
import { CreateProductWizard } from "../forms/CreateProductWizard/CreateProductWizard";
import type { default as ServerDataGridComponent } from "./ServerDataGrid";

// Types for CreateProductWizard (JS component)
interface ProductData {
  product_id?: string;
  title: string;
  asin: string;
  partNumber: string;
  families: Array<{ id: string; title: string }>;
}

interface SupplyUnit {
  upc: string;
  supply_title: string;
  part_number: string;
  inventory_id: string;
  inventory_title: string;
}

type ServerDataGridProps = React.ComponentProps<typeof ServerDataGridComponent>;

export const ListingsDataGrid = React.memo(function ListingsDataGrid({
  getRowId = (row) => row.product.product_id,
  enableDuplicate = false,
  enableCreate = false,
  enableEdit = true,
  enableDelete = true,
  ...otherProps
}: ServerDataGridProps) {
  // State for CreateProductWizard
  const [displayProductWizard, setDisplayProductWizard] = useState(false);
  const [initialProduct, setInitialProduct] = useState<ProductData | null>(
    null
  );
  const [productFormMode, setProductFormMode] = useState<
    "create" | "duplicate" | "edit"
  >("create");
  const [productFormPage, setProductFormPage] = useState(1);
  const [preselectedSupplyUnits, setPreselectedSupplyUnits] = useState<
    SupplyUnit[]
  >([]);
  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState(false);

  // Transform row data to the shape CreateProductWizard expects
  const shapeProductDataForWizard = useCallback((row: any): ProductData => {
    return {
      product_id: row.product?.product_id,
      title: row.product?.product_title || "",
      partNumber: row.product?.assembly_part_number || "",
      asin: row.product?.product_asin || "",
      families:
        row.product_families?.map((subRow: any) => ({
          id: subRow.id,
          title: subRow.family,
        })) || [],
    };
  }, []);

  // Handle create new
  const handleCreateNew = useCallback(() => {
    setInitialProduct(null);
    setPreselectedSupplyUnits([]);
    setProductFormMode("create");
    setProductFormPage(1);
    setDisplayProductWizard(true);
  }, []);

  // Handle duplicate
  const handleDuplicate = useCallback(
    (selectedRow: any) => {
      const productData = shapeProductDataForWizard(selectedRow);
      setInitialProduct(productData);
      setPreselectedSupplyUnits([]);
      setProductFormMode("duplicate");
      setProductFormPage(1);
      setDisplayProductWizard(true);
    },
    [shapeProductDataForWizard]
  );

  // Handle wizard close
  const closeCreateForm = useCallback((skipConfirmation?: boolean) => {
    if (skipConfirmation) {
      setDisplayProductWizard(false);
      setInitialProduct(null);
      setPreselectedSupplyUnits([]);
      setProductFormMode("create");
      setProductFormPage(1);
    } else {
      setConfirmCloseModalOpen(true);
    }
  }, []);

  const handleConfirmClose = useCallback(() => {
    setInitialProduct(null);
    setDisplayProductWizard(false);
    setConfirmCloseModalOpen(false);
    setPreselectedSupplyUnits([]);
    setProductFormMode("create");
    setProductFormPage(1);
  }, []);

  const handleConfirmCloseCancel = useCallback(() => {
    setConfirmCloseModalOpen(false);
  }, []);

  return (
    <>
      <Confirm
        open={confirmCloseModalOpen}
        content="Are you sure you want to close the create form? Any unsaved changes will be lost."
        onCancel={handleConfirmCloseCancel}
        onConfirm={handleConfirmClose}
      />

      {displayProductWizard && (
        <CreateProductWizard
          onClose={closeCreateForm}
          preselectedSupplyUnits={preselectedSupplyUnits}
          initialProduct={initialProduct}
          initialPage={productFormPage}
          mode={productFormMode}
        />
      )}

      <ServerDataGrid
        {...otherProps}
        getRowId={getRowId}
        enableCreate={enableCreate}
        enableDuplicate={enableDuplicate}
        enableEdit={enableEdit}
        enableDelete={enableDelete}
        // Override the default create/duplicate handlers
        onCreateClick={handleCreateNew}
        onDuplicateClick={handleDuplicate}
      />
    </>
  );
});
